import React, { useState, useEffect, useMemo } from "react";
import { Drawer, Form, Input, InputNumber, Select, Cascader, DatePicker, Row, Col, Typography, Flex, Button } from "antd";
import { PlusCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { amountFormatter, amountParser } from "../../../utils/formatters";
import { getCompanySettings } from "../../../services/organizationService";
import { getEmployees } from "../../../services/employeeService";
import { getCookie } from "../../../utils/cookieUtils";
import { buildMultiCompanyProductCascaderOptions, cascaderSearchFilter, resolveCascaderProduct } from "../../../utils/productCatalogUtils";

const { Title } = Typography;

function AddDealModal({ visible, onClose, onSaveDeal, employees = [], isEmployee = false, defaultEmployee = null, prefillLead = null }) {
  const [form] = Form.useForm();
  const reduxCompanyInfo = useSelector((state) => state.executor?.companyInfo);
  const user = useSelector((state) => state.session?.settings?.userInfo);
  const effectiveCompanyCode = reduxCompanyInfo?.companyCode || user?.companyCode || getCookie("companyCode") || "";
  const [mainProducts, setMainProducts] = useState([]);
  const [collaboratedGroups, setCollaboratedGroups] = useState([]);
  const [employeesList, setEmployeesList] = useState(employees || []);

  useEffect(() => {
    if (Array.isArray(employees) && employees.length > 0) {
      setEmployeesList(employees);
    }
  }, [employees]);

  useEffect(() => {
    if (!visible || !effectiveCompanyCode) return;
    if (Array.isArray(employees) && employees.length > 0) return;

    let isMounted = true;
    getEmployees(effectiveCompanyCode)
      .then((res) => {
        if (!isMounted) return;
        const list = res?.employees || res?.data || (Array.isArray(res) ? res : []);
        if (Array.isArray(list) && list.length > 0) {
          setEmployeesList(list);
        }
      })
      .catch((err) => {
        console.error("[AddDealModal] Failed to load employees:", err);
      });

    return () => {
      isMounted = false;
    };
  }, [visible, effectiveCompanyCode, employees]);

  useEffect(() => {
    if (!visible) return;

    let isMounted = true;

    async function loadCatalog() {
      if (!effectiveCompanyCode) return;

      try {
        let hostProducts = [];
        let hostSettings = null;

        if (Array.isArray(reduxCompanyInfo?.products) && reduxCompanyInfo.products.length > 0) {
          hostProducts = reduxCompanyInfo.products;
          hostSettings = reduxCompanyInfo;
        } else {
          const res = await getCompanySettings(effectiveCompanyCode);
          if (res?.success && res.settings) {
            hostProducts = Array.isArray(res.settings.products) ? res.settings.products : [];
            hostSettings = res.settings;
          }
        }

        if (!isMounted) return;
        setMainProducts(hostProducts);

        // Gather all collaborating companies from employee permissions and company settings
        const allowed = Array.isArray(user?.allowedCompanies) ? user.allowedCompanies : [];
        const collabs = Array.isArray(hostSettings?.collaboratingCompanies) ? hostSettings.collaboratingCompanies : [];
        const reduxCollabs = Array.isArray(reduxCompanyInfo?.collaboratingCompanies) ? reduxCompanyInfo.collaboratingCompanies : [];

        const rawList = [...allowed, ...collabs, ...reduxCollabs];
        const uniqueCollabCodes = Array.from(
          new Set(
            rawList
              .map((c) => (typeof c === "string" ? c.trim() : c?.companyCode ? String(c.companyCode).trim() : ""))
              .filter((c) => c && c.toUpperCase() !== String(effectiveCompanyCode).trim().toUpperCase())
          )
        );

        if (uniqueCollabCodes.length > 0) {
          const collabResponses = await Promise.allSettled(
            uniqueCollabCodes.map((code) => getCompanySettings(code))
          );

          if (!isMounted) return;

          const groups = [];
          collabResponses.forEach((res, index) => {
            const code = uniqueCollabCodes[index];
            if (res.status === "fulfilled" && res.value?.success && res.value.settings) {
              const partnerProducts = Array.isArray(res.value.settings.products) ? res.value.settings.products : [];
              if (partnerProducts.length > 0) {
                groups.push({
                  companyCode: code,
                  companyName: res.value.settings.companyName || code,
                  products: partnerProducts,
                });
              }
            }
          });

          setCollaboratedGroups(groups);
        } else {
          setCollaboratedGroups([]);
        }
      } catch (err) {
        console.error("[AddDealModal] Error loading multi-company product catalog:", err);
      }
    }

    loadCatalog();

    return () => {
      isMounted = false;
    };
  }, [visible, reduxCompanyInfo, effectiveCompanyCode, user?.allowedCompanies]);

  const productCascaderOptions = useMemo(
    () => buildMultiCompanyProductCascaderOptions(mainProducts, collaboratedGroups),
    [mainProducts, collaboratedGroups]
  );

  const hasAnyProducts = useMemo(() => {
    if (mainProducts.length > 0) return true;
    return collaboratedGroups.some((g) => Array.isArray(g.products) && g.products.length > 0);
  }, [mainProducts, collaboratedGroups]);

  const employeeOptions = useMemo(() => {
    const options = employeesList.map((e) => {
      const id = String(e._id || e.id || e.employeeId || "");
      const name = e.name || e.employeeName || "Sales Rep";
      return {
        value: id,
        label: e.designation ? `${name} (${e.designation})` : name,
      };
    });

    const assignedId = prefillLead?.assignedEmployeeId ? String(prefillLead.assignedEmployeeId) : null;
    if (assignedId && !options.some((opt) => opt.value === assignedId)) {
      const fallbackName = prefillLead.assignedEmployeeName || prefillLead.assignedEmployee || "Assigned Sales Rep";
      options.unshift({
        value: assignedId,
        label: fallbackName,
      });
    }

    return options;
  }, [employeesList, prefillLead]);

  const handleProductCascaderSelect = (value, selectedOptions) => {
    if (!value || value.length === 0) {
      return;
    }
    const resolved = resolveCascaderProduct(productCascaderOptions, value, selectedOptions);
    if (resolved) {
      form.setFieldsValue({
        amount: resolved.price || 0,
      });
    }
  };

  useEffect(() => {
    if (visible) {
      form.resetFields();
      if (prefillLead) {
        form.setFieldsValue({
          companyName: prefillLead.leadCompanyName || prefillLead.companyName || "",
          contactName: prefillLead.contactName || prefillLead.primaryContact || "",
          contactNumber: prefillLead.contactNumber || prefillLead.directorMobileNumber || "",
          assignedEmployeeId: prefillLead.assignedEmployeeId
            ? String(prefillLead.assignedEmployeeId)
            : isEmployee && defaultEmployee
            ? defaultEmployee.id
            : undefined,
        });
      } else if (isEmployee && defaultEmployee) {
        form.setFieldsValue({
          assignedEmployeeId: defaultEmployee.id,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, isEmployee, defaultEmployee?.id, prefillLead?._id || prefillLead?.id]);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleFinish = async (values) => {
    let assignedId = values.assignedEmployeeId;
    let assignedName = "Unassigned";

    if (isEmployee && defaultEmployee) {
      assignedId = defaultEmployee.id;
      assignedName = defaultEmployee.name || "Sales Rep";
    } else if (assignedId) {
      const assignedEmp = employeesList.find((e) => String(e._id || e.id || e.employeeId) === String(assignedId));
      assignedName = assignedEmp ? (assignedEmp.name || assignedEmp.employeeName) : (prefillLead?.assignedEmployeeName || values.assignedEmployeeName || "Sales Rep");
    }

    const resolvedDealName = Array.isArray(values.dealName)
      ? (resolveCascaderProduct(productCascaderOptions, values.dealName)?.displayName || values.dealName.join(" - "))
      : String(values.dealName || "").trim();

    const newDeal = {
      leadId: prefillLead?._id || prefillLead?.id || undefined,
      leadCompanyName: values.companyName,
      contactName: values.contactName,
      contactNumber: values.contactNumber,
      assignedEmployeeId: assignedId || null,
      assignedEmployeeName: assignedName,
      employeeId: assignedId || null,
      employeeName: assignedName,
      createdById: defaultEmployee?.id || null,
      createdByName: defaultEmployee?.name || null,
      dealName: resolvedDealName,
      amount: values.amount || 0,
      closingDate: values.closingDate ? values.closingDate.format("YYYY-MM-DD") : null,
      priority: values.priority || "Medium",
      pipelineStage: values.pipelineStage || "QUALIFICATION",
      description: values.description || "",
    };

    try {
      setIsSubmitting(true);
      if (onSaveDeal) {
        await onSaveDeal(newDeal);
      }
      form.resetFields();
      onClose();
    } catch (err) {
      console.error("Failed to save deal:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Drawer
      open={visible}
      onClose={onClose}
      width={520}
      destroyOnClose
      title={
        <Flex align="center" gap={8}>
          <PlusCircleOutlined style={{ color: "#2459d3", fontSize: "1.15rem" }} />
          <Title level={4} style={{ margin: 0, fontSize: "1.1rem" }}>
            Add New Pipeline Deal
          </Title>
        </Flex>
      }
      footer={
        <Flex justify="flex-end" gap={12} style={{ padding: "8px 0" }}>
          <Button onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="primary" loading={isSubmitting} onClick={() => form.submit()}>
            Create Deal
          </Button>
        </Flex>
      }
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        initialValues={{
          priority: "Medium",
          pipelineStage: "QUALIFICATION",
          closingDate: dayjs().add(30, "day"),
          assignedEmployeeId: isEmployee && defaultEmployee ? defaultEmployee.id : undefined,
        }}
        style={{ marginTop: 8 }}
      >
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item label="Company Name" name="companyName" rules={[{ required: true, message: "Company name is required" }]}>
              <Input placeholder="Client company name..." />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label="Deal Name (Product / Service)" name="dealName" rules={[{ required: true, message: "Please select product or enter deal name" }]}>
              {hasAnyProducts ? (
                <Cascader
                  placeholder="Select Product / Sub-Product..."
                  options={productCascaderOptions}
                  onChange={handleProductCascaderSelect}
                  changeOnSelect
                  showSearch={{ filter: cascaderSearchFilter }}
                  allowClear
                  style={{ width: "100%" }}
                />
              ) : (
                <Input placeholder="e.g. Enterprise Voice 50 Reps" />
              )}
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item label="Contact Person" name="contactName" rules={[{ required: true, message: "Contact person is required" }]}>
              <Input placeholder="Contact person..." />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label="Contact Phone" name="contactNumber">
              <Input placeholder="+91 98765 43210" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item label="Estimated Amount (₹)" name="amount" rules={[{ required: true, message: "Amount is required" }]}>
              <InputNumber min={0} placeholder="0" formatter={amountFormatter} parser={amountParser} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label="Priority" name="priority">
              <Select
                options={[
                  { value: "High", label: "High Priority" },
                  { value: "Medium", label: "Medium Priority" },
                  { value: "Low", label: "Low Priority" },
                ]}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={isEmployee ? 24 : 12}>
            <Form.Item label="Expected Close Date" name="closingDate">
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          {!isEmployee && (
            <Col xs={24} sm={12}>
              <Form.Item label="Assign Sales Rep" name="assignedEmployeeId">
                <Select
                  showSearch
                  optionFilterProp="label"
                  placeholder="Select assigned rep..."
                  allowClear
                  options={employeeOptions}
                />
              </Form.Item>
            </Col>
          )}
        </Row>

        <Form.Item label="Deal Scope & Notes" name="description">
          <Input.TextArea rows={3} placeholder="Requirements or context..." />
        </Form.Item>
      </Form>
    </Drawer>
  );
}

export default AddDealModal;
